/**
 * IndicatorTooltip.jsx — components/shared/IndicatorTooltip.jsx
 *
 * Hover tooltip explaining a technical indicator with dynamic
 * bullish/bearish signal based on the current value.
 *
 * Usage:
 *   <IndicatorTooltip id="ivRank" value={72} />
 *
 * Each indicator definition includes:
 *   label       — short display name
 *   explain     — what it measures (plain English)
 *   bullish(v)  — returns true when the value is bullish
 *   bearish(v)  — returns true when the value is bearish
 *   signal(v)   — returns { label, color, icon } for the current value
 *   format(v)   — display format
 */

import { useState, useRef, useEffect } from 'react';

// ── Indicator definitions ─────────────────────────────────────────────────────
export const INDICATORS = {
  ivRank: {
    label: 'IV Rank',
    explain: 'How elevated Implied Volatility is vs its own 52-week range. 100 = highest IV of the year. High IV Rank = options are expensive (good to sell premium). Low = options are cheap (good to buy).',
    bullish: v => v < 30,
    bearish: v => v > 70,
    signal: v => v > 70 ? { label: 'Elevated — sell premium', color: 'var(--down)', icon: '↓' }
                : v < 30 ? { label: 'Low — buy options cheap', color: 'var(--up)', icon: '↑' }
                : { label: 'Neutral range', color: 'var(--muted)', icon: '→' },
    format: v => v != null ? `${v}/100` : '—',
  },
  iv30: {
    label: 'IV30',
    explain: '30-day Implied Volatility (annualised %). The market\'s forecast of how much the stock will move over the next 30 days. Higher = more expected movement = more expensive options.',
    bullish: v => v < 20,
    bearish: v => v > 50,
    signal: v => v > 50 ? { label: 'High — options expensive', color: 'var(--down)', icon: '↑' }
                : v < 20 ? { label: 'Low — options cheap', color: 'var(--up)', icon: '↓' }
                : { label: 'Moderate', color: 'var(--muted)', icon: '→' },
    format: v => v != null ? `${Number(v).toFixed(1)}%` : '—',
  },
  impliedMove: {
    label: 'Implied Move',
    explain: 'The ±% move the options market is pricing in for earnings. Calculated from the at-the-money straddle price. If the actual move exceeds this, buying straddles was profitable.',
    bullish: () => false,
    bearish: () => false,
    signal: v => v > 8 ? { label: 'Very high — large move expected', color: 'var(--warn)', icon: '⚡' }
                : v > 4 ? { label: 'Elevated', color: 'var(--warn)', icon: '↑' }
                : { label: 'Modest expectation', color: 'var(--muted)', icon: '→' },
    format: v => v != null ? `±${Number(v).toFixed(1)}%` : '—',
  },
  historicalAvgMove: {
    label: 'Hist. Move',
    explain: 'Average actual earnings-day move over the last 6-8 reports (absolute %). Compare to Implied Move: if Hist > Implied, the market may be underpricing the move.',
    bullish: () => false,
    bearish: () => false,
    signal: (v, implied) => implied && v > implied * 1.1
      ? { label: 'Hist > Implied — may underpriced', color: 'var(--up)', icon: '↑' }
      : implied && v < implied * 0.9
        ? { label: 'Hist < Implied — vol may be rich', color: 'var(--down)', icon: '↓' }
        : { label: 'In line with implied', color: 'var(--muted)', icon: '→' },
    format: v => v != null ? `±${Number(v).toFixed(1)}%` : '—',
  },
  probUp: {
    label: 'Bull Prob',
    explain: 'Estimated probability of an upward move around earnings. Derived from options skew (put/call pricing imbalance) and analyst consensus. Above 55% leans bullish, below 45% leans bearish.',
    bullish: v => v >= 55,
    bearish: v => v <= 45,
    signal: v => v >= 60 ? { label: 'Bullish lean', color: 'var(--up)', icon: '↑' }
                : v <= 40 ? { label: 'Bearish lean', color: 'var(--down)', icon: '↓' }
                : { label: 'Even odds', color: 'var(--muted)', icon: '→' },
    format: v => v != null ? `${v}%` : '—',
  },
  oscillationScore: {
    label: 'Oscillation',
    explain: 'How frequently a stock reverses direction intraday (0–100). High = lots of up/down movement without persisting in one direction. Ideal for scalping both sides. Low = trending stock.',
    bullish: v => v >= 70,
    bearish: v => v < 40,
    signal: v => v >= 70 ? { label: 'Excellent for scalping', color: 'var(--up)', icon: '⟺' }
                : v >= 45 ? { label: 'Moderate oscillation', color: 'var(--warn)', icon: '↔' }
                : { label: 'Trending — risky to scalp both sides', color: 'var(--down)', icon: '→' },
    format: v => v != null ? `${v}/100` : '—',
  },
  trendStrength: {
    label: 'Trend Strength',
    explain: 'How strongly the stock is trending in one direction (0–100). LOW is good for scalping — it means the stock oscillates. HIGH means a persistent trend, which makes scalping both sides dangerous.',
    bullish: v => v < 30,
    bearish: v => v > 60,
    signal: v => v > 60 ? { label: 'Strong trend — scalp one side only', color: 'var(--down)', icon: '↑↑' }
                : v > 35 ? { label: 'Moderate trend', color: 'var(--warn)', icon: '↑' }
                : { label: 'Low trend — good oscillator', color: 'var(--up)', icon: '⟺' },
    format: v => v != null ? `${v}/100` : '—',
  },
  volatilityScore: {
    label: 'Vol Score',
    explain: 'Composite score (0–100) combining IV Rank (40%), Implied Move size (40%), and Historical Move (20%). Higher = more interesting from a volatility trading perspective.',
    bullish: () => false,
    bearish: () => false,
    signal: v => v >= 75 ? { label: 'High vol — active opportunities', color: 'var(--warn)', icon: '⚡' }
                : v >= 50 ? { label: 'Moderate', color: 'var(--muted)', icon: '↔' }
                : { label: 'Low vol environment', color: 'var(--faint)', icon: '→' },
    format: v => v != null ? `${v}/100` : '—',
  },
  spyTrend: {
    label: 'SPY Trend',
    explain: 'Whether SPY is in a persistent directional trend. A trending SPY reduces effectiveness of scalping both sides — one direction has an edge. Ranging/choppy SPY is ideal for neutral scalps.',
    bullish: v => v === 'ranging' || v === 'choppy',
    bearish: v => v === 'uptrend' || v === 'downtrend',
    signal: v => (v === 'ranging' || v === 'choppy')
      ? { label: 'Ranging — neutral scalps work', color: 'var(--up)', icon: '✅' }
      : { label: 'Trending — bias one direction', color: 'var(--down)', icon: '⚠️' },
    format: v => v || '—',
  },
  ma50: {
    label: '50-Day MA',
    explain: '50-day Simple Moving Average. Short-term trend benchmark. Price above 50MA = short-term bullish. Price below = short-term bearish. A key support/resistance level watched by institutions.',
    bullish: (v, price) => price > v,
    bearish: (v, price) => price < v,
    signal: (v, price) => !price || !v ? { label: 'No data', color: 'var(--faint)', icon: '—' }
      : price > v * 1.02 ? { label: 'Price well above — bullish', color: 'var(--up)', icon: '↑' }
      : price < v * 0.98 ? { label: 'Price below — bearish', color: 'var(--down)', icon: '↓' }
      : { label: 'Near the 50MA — watch for break', color: 'var(--warn)', icon: '→' },
    format: v => v != null ? `$${Number(v).toFixed(2)}` : '—',
  },
  ma200: {
    label: '200-Day MA',
    explain: '200-day Simple Moving Average. Long-term trend benchmark. The most widely watched MA. Price above = bull market regime. Price below = bear market regime. Crosses are major signals.',
    bullish: (v, price) => price > v,
    bearish: (v, price) => price < v,
    signal: (v, price) => !price || !v ? { label: 'No data', color: 'var(--faint)', icon: '—' }
      : price > v * 1.05 ? { label: 'Well above — bull regime', color: 'var(--up)', icon: '↑↑' }
      : price < v * 0.95 ? { label: 'Well below — bear regime', color: 'var(--down)', icon: '↓↓' }
      : price > v ? { label: 'Above — bullish', color: 'var(--up)', icon: '↑' }
      : { label: 'Below — bearish', color: 'var(--down)', icon: '↓' },
    format: v => v != null ? `$${Number(v).toFixed(2)}` : '—',
  },
  pivotP: {
    label: 'Pivot (P)',
    explain: 'Classic floor pivot point = (H + L + C) / 3 from the previous session. The primary equilibrium level. Price above P = intraday bullish bias. Below = bearish. Options scalpers use pivot levels as entry/exit zones.',
    bullish: (v, price) => price > v,
    bearish: (v, price) => price < v,
    signal: (v, price) => !price || !v ? { label: 'No data', color: 'var(--faint)', icon: '—' }
      : price > v ? { label: 'Above pivot — intraday bullish', color: 'var(--up)', icon: '↑' }
      : { label: 'Below pivot — intraday bearish', color: 'var(--down)', icon: '↓' },
    format: v => v != null ? `$${Number(v).toFixed(2)}` : '—',
  },
  vix: {
    label: 'VIX',
    explain: 'CBOE Volatility Index — measures 30-day implied vol of S&P 500 options. Called the "fear gauge". VIX > 25 = elevated fear/vol. VIX < 15 = complacency. Spiking VIX pre-market signals institutional put buying.',
    bullish: v => v < 15,
    bearish: v => v > 25,
    signal: v => v > 30 ? { label: 'Fear spike — volatility regime', color: 'var(--down)', icon: '⚠️' }
                : v > 20 ? { label: 'Elevated — cautious', color: 'var(--warn)', icon: '↑' }
                : v < 15 ? { label: 'Low — complacency', color: 'var(--up)', icon: '↓' }
                : { label: 'Normal range', color: 'var(--muted)', icon: '→' },
    format: v => v != null ? Number(v).toFixed(1) : '—',
  },
  preMarketVol: {
    label: 'Pre-Mkt Vol',
    explain: 'Pre-market volume as % of average daily volume. High pre-market volume (>2% ADV) indicates institutional positioning, usually driven by overnight news or earnings. This signals serious conviction.',
    bullish: () => false,
    bearish: () => false,
    signal: (v) => {
      if (v === 'heavy')    return { label: 'Heavy — institutional positioning', color: 'var(--down)', icon: '⚡' };
      if (v === 'elevated') return { label: 'Elevated — notable activity', color: 'var(--warn)', icon: '↑' };
      if (v === 'moderate') return { label: 'Moderate', color: 'var(--muted)', icon: '→' };
      return { label: 'Light — quiet pre-market', color: 'var(--faint)', icon: '→' };
    },
    format: v => v || '—',
  },
};

// ── Tooltip component ─────────────────────────────────────────────────────────
export function IndicatorTooltip({ id, value, secondaryValue, children }) {
  const [visible, setVisible] = useState(false);
  const [pos, setPos]         = useState({ x: 0, y: 0 });
  const ref = useRef(null);
  const def = INDICATORS[id];
  if (!def) return <>{children}</>;

  const sig = def.signal(value, secondaryValue);

  const show = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPos({ x: rect.left, y: rect.bottom + 6 });
    setVisible(true);
  };

  return (
    <>
      <span
        ref={ref}
        onMouseEnter={show}
        onMouseLeave={() => setVisible(false)}
        onFocus={show}
        onBlur={() => setVisible(false)}
        style={{ cursor: 'help', borderBottom: '1px dashed var(--faint)', display: 'inline' }}
      >
        {children || def.format(value)}
      </span>

      {visible && (
        <div style={{
          position: 'fixed',
          left: Math.min(pos.x, window.innerWidth - 290),
          top: pos.y,
          zIndex: 9999,
          width: 280,
          background: 'var(--bg-elev)',
          border: '1px solid var(--border-strong)',
          borderRadius: 7,
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
          padding: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
        }}>
          {/* Header */}
          <div style={{ padding: '8px 12px', background: 'var(--bg)', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text)', letterSpacing: '.05em' }}>{def.label}</span>
            <span style={{ fontFamily: 'monospace', fontSize: 12, fontWeight: 700, color: 'var(--muted)' }}>{def.format(value)}</span>
          </div>

          {/* Signal badge */}
          <div style={{ padding: '8px 12px', borderBottom: '1px solid var(--border)', background: `${sig.color}11`, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 14 }}>{sig.icon}</span>
            <span style={{ fontSize: 11, fontWeight: 700, color: sig.color }}>{sig.label}</span>
          </div>

          {/* Explanation */}
          <div style={{ padding: '10px 12px', fontSize: 11, color: 'var(--muted)', lineHeight: 1.6 }}>
            {def.explain}
          </div>
        </div>
      )}
    </>
  );
}

/** Wrap a panel title with a label that shows what the panel does */
export function PanelHelp({ title, help }) {
  const [show, setShow] = useState(false);
  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      {title}
      <span
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        style={{ fontSize: 10, color: 'var(--faint)', cursor: 'help', userSelect: 'none', lineHeight: 1 }}
      >ⓘ</span>
      {show && (
        <div style={{
          position: 'absolute', top: '120%', left: 0, zIndex: 9999,
          width: 240, background: 'var(--bg-elev)', border: '1px solid var(--border-strong)',
          borderRadius: 6, padding: '10px 12px', boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
          fontSize: 11, color: 'var(--muted)', lineHeight: 1.6, pointerEvents: 'none',
          whiteSpace: 'normal',
        }}>
          {help}
        </div>
      )}
    </span>
  );
}
